import { Module, ActionContext } from 'vuex';
import { RootStoreState } from '@/store';
import ApiServiceWrapper from '@/shared/api-request.service';
import { StoreInputParameters } from '@/services/idbStore.interface';
import idb, { TENANTS_STORE_NAME } from '@/services/indexedDb.service';
import { isBoolean } from 'lodash';

export interface DashBoardStoreState {
  allCustomers: any[];
  cssSwitch: any;
  activeFilterStatus: 'All' | 'Starred' | 'sharedToMe' | 'sharedByMe';
  viewType: 'Card' | 'List';
  sortBy: 'defcon_1-5' | 'defcon_5-1' | 'shield_1-10' | 'shield_10-1' | 'customerName';
  filteredCustomers: any[];
  searchedCustomerText: string;
  initial: {
    allCustomers: any[];
    activeFilterStatus: 'All' | 'Starred' | 'sharedToMe' | 'sharedByMe';
    viewType: 'Card' | 'List';
    sortBy: 'defcon_1-5' | 'defcon_5-1' | 'shield_1-10' | 'shield_10-1' | 'customerName';
    filteredCustomers: any[];
    searchedCustomerText: string;
  };
  widgetCustomerInfo: {
    customersCount: Number | String;
    productsCount: Number | String;
    totExpiredProducts: Number | String;
    totSoonExpiringProducts: Number | String;
  };
}

export interface UpdateEventObject {
  delete?: { customerId: string };
  edit?: { customerId: string, newName: string };
  starred?: { customerId: string, enable: boolean };
  default?: { customerId: string };
}

export default {
  state: {
    allCustomers: [],
    activeFilterStatus: 'All',
    viewType: 'List',
    cssSwitch: '',
    sortBy: 'customerName',
    filteredCustomers: [],
    searchedCustomerText: '',
    initial: {
      allCustomers: [],
      activeFilterStatus: 'All',
      viewType: 'List',
      sortBy: 'customerName',
      filteredCustomers: [],
      searchedCustomerText: '',
    },
    widgetCustomerInfo: {
      customersCount: '',
      productsCount: '',
      totExpiredProducts: 0,
      totSoonExpiringProducts: 0,
    },
  },
  mutations: {
    switchCssView(state, payload) {
      state.cssSwitch = payload.value;
    },
    updateWidgetInfo(state, response) {
      state.allCustomers = response;
      state.filteredCustomers = [...state.allCustomers];
    },
    sortCustomer: (state: any, sortBy?: string) => {
      if (sortBy) state.sortBy = sortBy;
      switch (state.sortBy) {
        case 'customerName':
          const customerList = state.filteredCustomers
            .filter((each: any) => each.isDefault === 'FALSE')
            .sort((a: any, b: any) => a.customerName.toLowerCase() < b.customerName.toLowerCase() ? -1 : 1);
          const defaultCustomer = state.filteredCustomers.filter((each: any) => each.isDefault === 'TRUE');
          if (defaultCustomer.length > 0) {
            state.filteredCustomers = [...defaultCustomer, ...customerList];
          } else {
            state.filteredCustomers = [...customerList];
          }
          break;
        case 'defcon_1-5': state.filteredCustomers.sort((a: any, b: any) => a.defCon - b.defCon); break;
        case 'defcon_5-1': state.filteredCustomers.sort((a: any, b: any) => b.defCon - a.defCon); break;
        case 'shield_1-10': state.filteredCustomers.sort((a: any, b: any) => a.shield - b.shield); break;
        case 'shield_10-1': state.filteredCustomers.sort((a: any, b: any) => b.shield - a.shield); break;
      }
    },
    filterCustomer: (state: any, payload) => {
      if (payload && payload.searchText !== undefined) state.searchedCustomerText = payload.searchText;
      state.filteredCustomers = state.allCustomers.filter((each: any) =>
        each.customerName.toLowerCase()
          .indexOf(state.searchedCustomerText.toLowerCase()) > -1);
      if (payload.filterBy) state.activeFilterStatus = payload.filterBy;
      if (state.activeFilterStatus === 'Starred') {
        state.filteredCustomers = state.filteredCustomers.filter((each: any) => each.isStarred);
      }
      if (state.activeFilterStatus === 'sharedToMe') {
        state.filteredCustomers = state.filteredCustomers.filter((customer: any) => {
          return customer.isSharedProductGroup === 'YES';
        });
      }
      if (state.activeFilterStatus === 'sharedByMe') {
        state.filteredCustomers = state.filteredCustomers.filter((customer: any) => {
          return customer.isSharedProductGroup === 'NO';
        });
      }
    },
    updateStarCustomer: (state: any, payload: any) => {
      state.filteredCustomers[payload.i].isStarred = payload.status;
    },
    deleteCustomer: (state: any, index: number) => {
      const removedCustomers = state.filteredCustomers.splice(index, 1);
      if (removedCustomers.length) {
        state.allCustomers = state.allCustomers.filter((customer: any) => {
          return removedCustomers[0].customerName.toLowerCase() !== customer.customerName.toLowerCase();
        });
      }
    },
    mutate: (state: any, payload: any) => state[payload.property] = payload.with,
    renameCustomer: (state: any, payload: any) => state.filteredCustomers[payload.index].customerName = payload.with,
    assigner(state: any, payload) {
      try { state.appVersion; } catch (e) { }
      state[payload] = state.initial[payload];
    },
    updateProductmgmtWidgetInfo: (state: any, payload: any) => {
      state.widgetCustomerInfo.customersCount = payload['customersCount'];
      state.widgetCustomerInfo.productsCount = payload['productsCount'];
      state.widgetCustomerInfo.totSoonExpiringProducts = payload['totSoonExpiringProducts'];
      state.widgetCustomerInfo.totExpiredProducts = payload['totExpiredProducts'];
    },
    sortCustomerByStar: (state: any) => {
      const starredCustomerList = state.filteredCustomers
        .filter((each: any) => each.isDefault === 'FALSE' && each.isStarred)
        .sort((a: any, b: any) => a.customerName.toLowerCase() < b.customerName.toLowerCase() ? -1 : 1);
      const unStarredCustomerList = state.filteredCustomers
        .filter((each: any) => each.isDefault === 'FALSE' && !each.isStarred)
        .sort((a: any, b: any) => a.customerName.toLowerCase() < b.customerName.toLowerCase() ? -1 : 1);
      const defaultCustomer = state.filteredCustomers.filter((each: any) => each.isDefault === 'TRUE');
      state.filteredCustomers = [...defaultCustomer, ...starredCustomerList, ...unStarredCustomerList];
    },
  },
  actions: {
    resetStore(context: ActionContext<DashBoardStoreState, RootStoreState>) {
      const keys = context.state.initial ? Object.keys(context.state.initial) : [];
      keys.forEach((stateT: any) => {
        if (stateT !== 'initial') context.commit('assigner', stateT);
      });
    },
    // populateCustomerWidgets(context: ActionContext<DashBoardStoreState, RootStoreState>) {
    //   context.commit('startLoading', null, { root: true });
    //   const endpoint = 'users/get-customer-widget-info';
    //   return (ApiServiceWrapper.get(endpoint));
    // },
    async populateCustomerWidgets(context: ActionContext<DashBoardStoreState, RootStoreState>, forceFetch = false) {
      let endpoint = 'users/get-customer-widget-info';
      if ((forceFetch.hasOwnProperty('isFromMssp'))) {
        endpoint += '?forced=YES&isfromMSSPPage=YES';
      }
      if (isBoolean(forceFetch) && forceFetch) {
        endpoint += '?forced=YES';
      }
      // context.commit('startLoading', null, { root: true });
      if (!context.rootGetters.isLargeCustomer) {
        const apiData = await (ApiServiceWrapper.get(endpoint));
        return apiData.content;
      }
      const request: StoreInputParameters = { endpoint, forceFetch, payload: {}, objectName: TENANTS_STORE_NAME, httpMethod: 'get' };
      const idbData = await idb.getData(request, ['MSWAPP:SUCCESS'], 'content');
      return Promise.resolve(idbData);
    },
    async saveCustomerWidgets({ }, payload) {
      await idb.saveData(TENANTS_STORE_NAME, payload);
      return;
    },
    updateStarCustomerInDB(context: ActionContext<DashBoardStoreState, RootStoreState>, payload: any) {
      context.commit('startLoading', null, { root: true });
      const endpoint = 'product/starred-product-group';
      const body = {
        starProductGroup: [
          payload,
        ],
      };
      return ApiServiceWrapper.post(endpoint, body);
    },
    deleteCustomerInDB(context: ActionContext<DashBoardStoreState, RootStoreState>, payload: any) {
      context.commit('startLoading', null, { root: true });
      const endpoint = 'users/delete-productgroup';
      const body = {
        bsendMail: true,
        productGroupList: [
          payload,
        ],
      };
      return ApiServiceWrapper.post(endpoint, body);
    },
    renameCustomerInDB(context: ActionContext<DashBoardStoreState, RootStoreState>, payload: any) {
      context.commit('startLoading', null, { root: true });
      const endpoint = 'users/rename-product-group';
      return ApiServiceWrapper.post(endpoint, payload);
    },
    updateAppName(context: ActionContext<DashBoardStoreState, RootStoreState>, payload: any) {
      context.commit('startLoading', null, { root: true });
      const endpoint = `change-appname?appName=${payload}`;
      return ApiServiceWrapper.get(endpoint);
    },
  },
  getters: {
    getCustomerList: state => state.allCustomers,
    getSearchedCustomer: state => state.searchedCustomerText,
    getSwitchCss: state => state.cssSwitch,
  },
  namespaced: true,
} as Module<DashBoardStoreState, RootStoreState>;
